import { Warning } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import * as turf from '@turf/turf';
import { chain } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Zone } from '@helpers/types.ts';

interface ICombineZoneProps {
  zones: Array<Zone>;
  onClose: () => void;
  onSuccess: (areas: Array<Zone>, name: string) => void;
}

export const CombineZone = (props: ICombineZoneProps) => {
  const [name, setName] = useState('');
  const [checked, setChecked] = useState<Array<string>>([]);

  const onToggle = useCallback(
    (value: string) => {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setChecked(newChecked);
    },
    [checked],
  );

  const selected_areas = useMemo(() => {
    return chain(props.zones)
      .filter((area) => checked.includes(area._id))
      .value();
  }, [checked]);

  const isValid = useMemo(() => {
    let valid = true;
    for (const area_a of selected_areas) {
      let intersects = 0;
      for (const area_b of selected_areas) {
        if (area_a._id !== area_b._id) {
          // @ts-ignore
          if (turf.booleanOverlap(area_a.geometry, area_b.geometry)) {
            intersects++;
          }
        }
      }
      if (intersects == 0) {
        valid = false;
        return false;
      }
    }
    return valid;
  }, [checked, selected_areas]);

  return (
    <Dialog open={true} onClose={props.onClose} fullWidth maxWidth="xs">
      <DialogTitle>Combinar zonas</DialogTitle>
      <Divider />
      <DialogContent sx={{ paddingTop: 1, paddingBottom: 2 }}>
        <TextField
          autoFocus
          fullWidth
          value={name}
          autoComplete={'off'}
          variant="standard"
          label="Nuevo nombre de zona"
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <List sx={{ maxHeight: 300, overflow: 'auto' }}>
        {chain(props.zones)
          .sortBy((f) => f.name)
          .map((value) => {
            // @ts-ignore
            return (
              <ListItemButton
                dense
                key={value._id}
                role={undefined}
                onClick={() => onToggle(value._id)}
              >
                <ListItemIcon>
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={checked.indexOf(value._id) !== -1}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText id={value._id} primary={value.name} />
              </ListItemButton>
            );
          })
          .value()}
      </List>
      {!isValid && (
        <Alert color="warning" icon={<Warning />}>
          Las zonas seleccionadas deben tener una intersección en algún punto
        </Alert>
      )}
      <Divider />
      <DialogActions>
        <Button color="error" onClick={props.onClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={name.length < 4 || !isValid}
          onClick={() => props.onSuccess(selected_areas, name)}
        >
          Combinar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
